import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { createLLMOMeta, createBreadcrumbSchema, createToolSchema, createHowToSchema, createFAQSchema } from "@/lib/seo"
import { getToolBySlug } from "@/data/tools/tools-registry"
import ToolPageLayout from "@/components/tools/ToolPageLayout"
import ToolResultCard from "@/components/tools/ToolResultCard"
import { NumberField, SelectField, SubmitButton, ResetButton } from "@/components/tools/ToolFormField"
import { SectionHeading, Paragraph, FeatureGrid, TierCards, TipList, ComparisonTable, ContentSection } from "@/components/tools/SeoContent"

const tool = getToolBySlug("instagram-influencer-pricing-calculator")!

const NICHES = [
  { value: "fashion-beauty", label: "Fashion & Beauty", m: 1.3 },
  { value: "fitness-health", label: "Fitness & Health", m: 1.2 },
  { value: "travel-lifestyle", label: "Travel & Lifestyle", m: 1.2 },
  { value: "food-cooking", label: "Food & Cooking", m: 1.1 },
  { value: "tech-gadgets", label: "Tech & Gadgets", m: 1.5 },
  { value: "business-finance", label: "Business & Finance", m: 2.0 },
  { value: "parenting-family", label: "Parenting & Family", m: 1.0 },
  { value: "gaming-esports", label: "Gaming & Esports", m: 1.3 },
  { value: "photography-art", label: "Photography & Art", m: 1.1 },
  { value: "general-lifestyle", label: "General Lifestyle", m: 1.0 },
]

const CONTENT_TYPES = [
  { value: "feed-post", label: "Feed Post", m: 1.0 },
  { value: "story", label: "Story (24 hours)", m: 0.5 },
  { value: "reel", label: "Reel", m: 1.35 },
  { value: "video", label: "IGTV / Video", m: 1.35 },
  { value: "carousel", label: "Carousel Post", m: 1.15 },
]

export const Route = createFileRoute("/tools/instagram-influencer-pricing-calculator")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: tool.seo.title,
        description: tool.seo.description,
        url: "https://zapify.pro/tools/instagram-influencer-pricing-calculator",
      }),
      { name: "keywords", content: tool.seo.keywords.join(", ") },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/tools/instagram-influencer-pricing-calculator" }],
    scripts: [
      createToolSchema({ name: tool.name, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-influencer-pricing-calculator" }),
      createBreadcrumbSchema({ itemListElement: [
        { position: 1, name: "Home", item: "https://zapify.pro" },
        { position: 2, name: "Free Tools", item: "https://zapify.pro/tools" },
        { position: 3, name: tool.shortName, item: "https://zapify.pro/tools/instagram-influencer-pricing-calculator" },
      ]}),
      createHowToSchema({ name: "How to Calculate Your Influencer Pricing", description: tool.seo.description, steps: tool.howToSteps }),
      createFAQSchema(tool.faq),
    ],
  }),
  component: InfluencerPricingPage,
})

function InfluencerPricingPage() {
  const [followers, setFollowers] = useState("")
  const [engagement, setEngagement] = useState("")
  const [niche, setNiche] = useState("general-lifestyle")
  const [contentType, setContentType] = useState("feed-post")
  const [result, setResult] = useState<{ low: number; mid: number; high: number } | null>(null)

  const calculate = () => {
    const f = Number(followers)
    const er = Number(engagement)
    if (!f || f < 100 || !er) return

    const nicheData = NICHES.find((n) => n.value === niche)!
    const contentData = CONTENT_TYPES.find((c) => c.value === contentType)!

    const baseCPM = 15
    const baseRate = (f / 1000) * baseCPM

    // Engagement bonus: if micro (<100K) needs 5%+, larger needs 3%+
    const threshold = f < 100_000 ? 5 : 3
    const engagementBonus = er > threshold ? 1.25 : 1.0

    const mid = baseRate * nicheData.m * contentData.m * engagementBonus
    setResult({ low: mid * 0.8, mid, high: mid * 1.2 })
  }

  const fmt = (n: number) => {
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`
    return `$${Math.round(n)}`
  }

  return (
    <ToolPageLayout
      tool={tool}
      content={
        <div className="space-y-10">
          <ContentSection>
            <SectionHeading>How Much Should Influencers Charge?</SectionHeading>
            <Paragraph>
              The influencer marketing industry is projected to exceed $24 billion globally, yet pricing remains one of the most confusing aspects of the creator economy. Whether you are a nano influencer just landing your first brand deal or a macro creator renegotiating annual contracts, knowing your market rate is critical. Charge too little and you undervalue your audience, leaving thousands of dollars on the table over a career. Charge too much without the data to back it up and brands will move on to someone else.
            </Paragraph>
            <Paragraph className="mt-3">
              The reality is that influencer pricing is not a fixed number. It shifts based on your follower count, engagement quality, content niche, the type of deliverable, and the rights the brand wants. This calculator synthesizes all of these variables using up-to-date industry benchmarks so you walk into every negotiation armed with data, not guesswork.
            </Paragraph>
          </ContentSection>

          <ContentSection>
            <SectionHeading>Influencer Pricing by Tier</SectionHeading>
            <Paragraph>
              Industry rates vary widely, but the following tiers provide median ranges for a single Instagram feed post in a general lifestyle niche. Rates increase further for high-CPM niches like finance and technology.
            </Paragraph>
            <TierCards tiers={[
              { label: "Nano", range: "1K - 10K followers", description: "$10 - $100 per post, $5 - $50 per story. Ideal for hyper-local or niche campaigns with high engagement rates.", color: "green" },
              { label: "Micro", range: "10K - 50K followers", description: "$100 - $500 per post, $50 - $250 per story. The sweet spot for brands seeking authentic engagement and strong conversion rates.", color: "blue" },
              { label: "Mid-Tier", range: "50K - 200K followers", description: "$500 - $2,500 per post, $250 - $1,250 per story. Professional creators with proven track records and consistent content quality.", color: "yellow" },
              { label: "Macro", range: "200K - 1M followers", description: "$2,500 - $10,000 per post, $1,250 - $5,000 per story. Established influencers with broad reach and polished production value.", color: "green" },
              { label: "Mega", range: "1M+ followers", description: "$10,000+ per post, $5,000+ per story. Celebrity-tier creators commanding premium rates with massive audience reach.", color: "green" },
            ]} />
            <Paragraph className="mt-3">
              These ranges assume a general lifestyle niche with average engagement. Creators in premium verticals such as business, finance, or technology routinely command 1.5x to 2x the rates listed above, while creators with exceptionally high engagement rates can push even higher.
            </Paragraph>
          </ContentSection>

          <ContentSection>
            <SectionHeading>Key Factors That Affect Instagram Pricing</SectionHeading>
            <Paragraph>
              No single metric determines your rate. Brands evaluate a combination of quantitative signals and qualitative value when deciding what to pay. Here are the five most influential factors.
            </Paragraph>
            <FeatureGrid columns={2} items={[
              { icon: "\uD83D\uDC65", title: "Follower Count", description: "Follower count establishes the baseline reach a brand can expect. It is the starting point for every rate calculation, typically expressed as a cost-per-mille (CPM) model where creators charge a set amount per 1,000 followers. While follower count alone does not determine value, it sets the floor for negotiations and helps brands estimate the potential exposure of a collaboration." },
              { icon: "\uD83D\uDCCA", title: "Engagement Rate", description: "Engagement rate is the quality multiplier that separates average creators from premium ones. A micro influencer with a 7% engagement rate often delivers more conversions than a macro influencer with 1.5%. Brands know this, which is why high engagement justifies premium pricing. If your engagement rate is above 5% for accounts under 100K or above 3% for larger accounts, you should confidently charge at the higher end of your tier." },
              { icon: "\uD83C\uDFAF", title: "Niche Premium", description: "Not all audiences are created equal when it comes to advertising value. Finance, business, and technology niches command the highest premiums because brands in those verticals have larger marketing budgets and higher customer lifetime values. A finance influencer with 50K followers can reasonably charge what a general lifestyle creator with 150K followers charges because each follower in the finance niche is worth more to advertisers." },
              { icon: "\uD83C\uDFAC", title: "Content Type", description: "The deliverable you create directly impacts your rate. Reels and video content require more production effort, skill, and editing time than a static feed post, so they command higher rates. Stories, by contrast, are ephemeral and lower-effort, which is reflected in their lower price point. Brands understand these differences and expect to see pricing that matches the complexity and shelf life of each format." },
              { icon: "\uD83D\uDCDC", title: "Usage Rights & Exclusivity", description: "Usage rights are one of the most overlooked revenue levers for creators. When a brand wants to repurpose your content in their own ads, on their website, or across other channels, you should charge 50% to 100% on top of your base rate. Exclusivity clauses, which prevent you from working with competing brands for a set period, should add another 30% to 50%. These add-ons can double your total deal value and should always be negotiated separately from the base rate." },
            ]} />
          </ContentSection>

          <ContentSection>
            <SectionHeading>Content Type Pricing Guide</SectionHeading>
            <Paragraph>
              Different content formats carry different price tags because they require varying levels of effort, skill, and production value. Understanding these multipliers will help you price each deliverable accurately.
            </Paragraph>
            <ComparisonTable
              headers={["Content Type", "Rate Multiplier", "Notes"]}
              rows={[
                { method: "Reels", values: ["+20-50%", "Instagram's priority format with algorithmic amplification. Requires scripting, filming, and editing. A $500 feed post becomes $600-$750 for a Reel."] },
                { method: "Stories", values: ["30-50% of post rate", "Ephemeral 24-hour content. Excellent for swipe-up links, polls, and driving direct traffic. Price at one-third to one-half of your feed post rate."] },
                { method: "Carousels", values: ["+15%", "Higher engagement than single-image posts due to swiping. More design work required, so a modest 15% premium is standard."] },
                { method: "IGTV / Long-form Video", values: ["+35%", "Similar to Reels in production but longer and more in-depth. Ideal for tutorials, reviews, and detailed product showcases."] },
              ]}
            />
          </ContentSection>

          <ContentSection>
            <SectionHeading>Tips for Negotiating Brand Deals</SectionHeading>
            <Paragraph>
              Knowing your rate is only half the equation. How you present and negotiate that rate determines whether you close the deal at full price or get talked down. Here are five actionable strategies to maximize your earnings.
            </Paragraph>
            <TipList tips={[
              { title: "Lead with data, not feelings", description: "Share your media kit with engagement rates, audience demographics, and past campaign results. Brands pay more when they see proof that your audience converts. Numbers like swipe-up click-through rates, saves-to-likes ratios, and past brand ROI are the strongest negotiation tools." },
              { title: "Quote higher than your minimum", description: "Always leave room for negotiation by quoting 20-30% above your bottom line. Brands expect to negotiate down, so if your minimum is $500, quote $650. This way, even a discounted deal lands at a rate you are comfortable with." },
              { title: "Bundle content packages", description: "Instead of selling a single post, offer a package that includes a Reel, two story frames, and a feed post at a bundled rate. Packages increase the total deal value while giving the brand a discount on individual pieces, making it a win-win structure." },
              { title: "Separate usage rights and exclusivity", description: "Never include usage rights or exclusivity in your base rate for free. These should be line items on your invoice. A brand that wants to run your content as a paid ad is getting significantly more value than an organic post, and they should pay accordingly." },
              { title: "Set payment terms upfront", description: "Require 50% upfront before creating any content and the remaining 50% before the content goes live. This protects you from scope creep and non-payment, which are unfortunately common in the influencer space, especially with smaller brands and agencies." },
            ]} />
          </ContentSection>
        </div>
      }
    >
      <div className="max-w-2xl mx-auto">
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
          {!result ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <NumberField label="Followers" value={followers} onChange={setFollowers} placeholder="e.g. 25000" required min={100} />
                <NumberField label="Engagement Rate (%)" value={engagement} onChange={setEngagement} placeholder="e.g. 4.5" required min={0.1} step={0.1} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <SelectField
                  label="Niche"
                  value={niche}
                  onChange={setNiche}
                  options={NICHES.map(n => ({ value: n.value, label: n.label }))}
                  required
                />
                <SelectField
                  label="Content Type"
                  value={contentType}
                  onChange={setContentType}
                  options={CONTENT_TYPES.map(c => ({ value: c.value, label: c.label }))}
                  required
                />
              </div>
              <SubmitButton onClick={calculate} disabled={!followers || !engagement}>Calculate Pricing</SubmitButton>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="text-center">
                <p className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">Your Estimated Rate</p>
                <p className="text-4xl sm:text-5xl font-bold text-neutral-900 dark:text-white">{fmt(result.mid)}</p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">per {CONTENT_TYPES.find((c) => c.value === contentType)?.label}</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <ToolResultCard label="Low Estimate" value={fmt(result.low)} variant="warning" />
                <ToolResultCard label="Mid Estimate" value={fmt(result.mid)} variant="primary" />
                <ToolResultCard label="High Estimate" value={fmt(result.high)} variant="success" />
              </div>
              <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center">
                Add 50-100% for usage rights. Add 30-50% for exclusivity.
              </p>
              <ResetButton onClick={() => setResult(null)} />
            </div>
          )}
        </div>
      </div>
    </ToolPageLayout>
  )
}
